// Zeitraster für Tag-/Wochen-Ansicht des Kalenders (/calendar).
//
// Kern-Unterschied zur früheren Listen-Darstellung: Termine sind nach Uhrzeit
// POSITIONIERT (top = Start, height = Dauer) — Lücken im Tag sind damit sichtbar,
// statt weggeklappt zu werden. Überlappungen liegen nebeneinander.
//
// Die gesamte Mathematik (Schneiden, Packen, Stunden-Fenster, Snap, Pixel→Minute)
// steht JSX-frei in ./calendarLayout.ts und ist dort per Dry-Run abgesichert
// (frontend/calendar-grid-dryrun.mts). Diese Datei macht nur Darstellung + Zeiger-
// Interaktion.
import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import clsx from "clsx";
import { useT } from "../i18n";
import {
  BASE_FROM_HOUR,
  dateAtMinute,
  hourRange,
  layoutDay,
  minuteAt,
  offHoursBands,
  slotFromDrag,
  type GridEvent,
  type HourWindow,
} from "./calendarLayout";

/** Felder, die das Raster rendert. BEWUSST ein eigener Minimal-Kontrakt statt
 *  `CalEvent` aus pages/CalendarMs (Muster nextMeeting.ts): das Raster bleibt
 *  ohne Seiten-Import, und der generische Parameter `T` gibt der Seite in den
 *  Callbacks ihr volles Event-Objekt zurück. */
export interface TimeGridEvent extends GridEvent {
  subject: string;
  location?: string | null;
  is_cancelled?: boolean | null;
}

export interface CalendarTimeGridProps<T extends TimeGridEvent> {
  /** 1 Spalte (Tag) oder 7 (Woche), jeweils 00:00 lokal. */
  days: Date[];
  events: T[];
  loading?: boolean;
  /** Pixel pro Stunde — Tag darf luftiger sein als Woche. */
  hourPx?: number;
  /** Wochentags-Kopfzeile (Woche ja, Tag nein — dort steht das Datum im Kopf). */
  showDayHeaders?: boolean;
  onOpenEvent: (ev: T) => void;
  /** Geschäftszeiten des Nutzers — alles davor/danach wird abgeblendet.
   *
   *  BEWUSST ein Pflicht-Prop ohne Vorgabewert: der Default 9-17 gehört
   *  `ui/calendarBusinessHours.ts` (dieselbe Zahl wie serverseitig in
   *  `app/schemas/settings.py`). Ein zweiter Default hier hieße, dass eine
   *  vergessene Aufrufstelle still auf eine eigene Zahl fällt statt den
   *  Type-Check zu brechen. */
  businessHours: HourWindow;
  /** Klick/Ziehen in eine freie Fläche → neuer Termin mit diesem Fenster.
   *  `at` = Loslass-Punkt in Client-Koordinaten, damit der Anlege-Dialog dort
   *  aufgehen kann, wo der Nutzer geklickt hat (statt in der Viewport-Mitte). */
  onCreateSlot: (start: Date, end: Date, at: { x: number; y: number }) => void;
  /** Klick auf den Wochentags-Kopf (Woche → Tag). */
  onPickDay?: (day: Date) => void;
}

const GUTTER = "w-14";
/** Ab dieser Blockhöhe passen Zeit UND Betreff untereinander (2 Zeilen à ~15 px
 *  + Padding); darunter wird einzeilig „HH:MM Betreff" gerendert. */
const TWO_LINE_PX = 34;
/** Ab hier passt zusätzlich der Ort. */
const THREE_LINE_PX = 62;
/** Sichtbare Mindesthöhe, damit auch ein 5-Minuten-Termin klickbar bleibt. */
const MIN_BLOCK_PX = 16;

/**
 * Auszeichnung der Randzeiten (außerhalb der Geschäftszeiten).
 *
 * Zwei Dinge muss der Mitarbeiter im Raster unterscheiden können, und sie
 * brauchen zwei verschiedene Mittel:
 *
 *  1. **WO der Arbeitstag endet** — das ist eine Kante, keine Fläche. Sie liegt
 *     immer auf einer vollen Stunde (die Auswahlfelder geben nur ganze Stunden
 *     her), fällt also mit einer Stundenlinie zusammen und muss sich von ihr
 *     abheben, sonst ist sie nur eine weitere Linie unter dreizehn. Deshalb
 *     ZWEI Pixel und `border-strong`. Die 2 px tragen die Unterscheidung, die
 *     Farbe verstärkt sie nur: `--border-strong` ist NICHT in jedem Theme von
 *     `--border` verschieden (themes.css:206-207, warm/dunkel: identisch) —
 *     über die Strichbreite ist die Grenze in jedem der sechs Theme-Zustände
 *     zu erkennen.
 *  2. **WELCHE SEITE draußen ist** — das kann nur die Fläche sagen.
 *
 * Die Fläche tönt mit NEUTRALEM Schwarz, nicht mit `bg-surface-2`. Das ist der
 * eigentliche Fund an dieser Stelle: `--surface-2` ist im Theme `weiss`
 * IDENTISCH zu `--surface` (themes.css:97 „auch Sidebar/Wells reinweiß") und im
 * Dunkeln nur 3/255 davon entfernt — das geerbte `bg-surface-2/70` blendete
 * also je nach Theme sichtbar bis überhaupt nicht ab. Schwarz mit Deckkraft
 * hängt an keinem Token-Verhältnis und tönt über jedem der drei Presets gleich
 * verlässlich; die Wochenend-Tönung der Spalte (`bg-surface-2/30`) bleibt
 * darunter sichtbar, weil getönt und nicht überdeckt wird.
 *
 * Getrennte Deckkraft je Modus, weil „zurückgenommen" in beiden Modi
 * „dunkler" heißt, die Ausgangshelligkeit aber weit auseinanderliegt: über Weiß
 * genügen 5 %, über einer Dunkel-Fläche (22/25/28) braucht es 30 %, um
 * überhaupt anzukommen. Beide Werte sind im Walk je Theme GEMESSEN, nicht
 * geschätzt (Abschnitt P).
 *
 * Verworfen: Schraffur (repeating-linear-gradient). Sie trägt im Wochenraster
 * mit sieben Spalten mehr Unruhe hinein als Information — die Kante beantwortet
 * die Frage „ab wann" schon exakt.
 *
 * KEIN z-index: die Fläche liegt damit unter den Termin-Blöcken (z-10). Ein
 * Termin in der Randzeit wird also NICHT mitgetönt — sonst würde diese
 * Auszeichnung Lesbarkeit gegen Orientierung tauschen.
 */
const OFFHOURS_TINT =
  "pointer-events-none absolute inset-x-0 border-border-strong bg-black/[0.05] dark:bg-black/30";

function fmtHM(d: Date): string {
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
function hm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

export function CalendarTimeGrid<T extends TimeGridEvent>({
  days,
  events,
  loading = false,
  hourPx = 48,
  showDayHeaders = true,
  onOpenEvent,
  businessHours,
  onCreateSlot,
  onPickDay,
}: CalendarTimeGridProps<T>) {
  const t = useT();
  const layouts = useMemo(
    () => days.map((d) => layoutDay(events, d)),
    [days, events]
  );
  const { fromHour, toHour } = useMemo(() => hourRange(layouts), [layouts]);
  const hours = useMemo(
    () => Array.from({ length: toHour - fromHour }, (_, i) => fromHour + i),
    [fromHour, toHour]
  );
  const hasAllDay = layouts.some((l) => l.allDay.length > 0);
  // Randzeiten-Bänder in STUNDEN (Pixel erst unten mit `effHourPx`).
  const offHours = useMemo(
    () => offHoursBands(fromHour, toHour, businessHours),
    [fromHour, toHour, businessHours]
  );

  // Stundenhöhe folgt dem Container (Content-First): passt das Fenster ganz in
  // die Restfläche, werden die Stundenzeilen auf diese Höhe GEDEHNT — statt
  // unter der letzten Zeile weißen Rest zu lassen. Reicht die Höhe nicht (viele
  // Stunden / kleines Fenster), bleibt `hourPx` die Basisdichte und es wird
  // gescrollt. `effHourPx` ist ab hier die EINZIGE Umrechnung Minute ⇄ Pixel
  // (Blockhöhen, Linien, Klick-Position) — sonst würden Klick und Darstellung
  // auseinanderlaufen.
  const [viewportPx, setViewportPx] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setViewportPx(el.clientHeight));
    ro.observe(el);
    setViewportPx(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  const effHourPx =
    viewportPx > 0
      ? Math.max(hourPx, Math.floor(viewportPx / hours.length))
      : hourPx;
  const totalPx = hours.length * effHourPx;

  // Zeiger-Interaktion: Ziehen legt ein Fenster auf, Hover zeigt vorab, wo ein
  // Klick landen würde (macht „Klick = neuer Termin" ohne Erklärtext sichtbar).
  const [drag, setDrag] = useState<{ dayIdx: number; from: number; to: number } | null>(
    null
  );
  const [hover, setHover] = useState<{ dayIdx: number; min: number } | null>(null);

  // „Jetzt"-Linie minütlich nachziehen.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Startposition = 07:00. Nur wenn das Fenster wegen Rand-Terminen früher
  // beginnt, wird gescrollt — die Kernzeit steht dann trotzdem oben, die frühen
  // Termine sind nach oben erreichbar (nichts ist versteckt).
  const daysKey = days.map((d) => d.getTime()).join(",");
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = Math.max(0, (BASE_FROM_HOUR - fromHour) * effHourPx);
  }, [daysKey, fromHour, effHourPx]);

  const minAt = (e: React.PointerEvent<HTMLDivElement>): number => {
    const rect = e.currentTarget.getBoundingClientRect();
    return minuteAt(e.clientY - rect.top, effHourPx, fromHour);
  };

  const finishDrag = (
    d: { dayIdx: number; from: number; to: number },
    at: { x: number; y: number }
  ) => {
    const { startMin, endMin } = slotFromDrag(d.from, d.to);
    const day = days[d.dayIdx];
    if (day)
      onCreateSlot(dateAtMinute(day, startMin), dateAtMinute(day, endMin), at);
  };

  const top = (min: number) => (min / 60 - fromHour) * effHourPx;

  return (
    <div data-cal-grid className="flex h-full min-h-0 flex-col">
      {/* Wochentags-Kopf */}
      {showDayHeaders && (
        <div className="flex shrink-0 border-b border-border">
          <div className={clsx(GUTTER, "shrink-0")} />
          {days.map((d) => {
            const isToday = sameDay(d, now);
            const weekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <button
                key={d.getTime()}
                type="button"
                onClick={() => onPickDay?.(d)}
                title={t.calendar.termin.tagOeffnen}
                className={clsx(
                  "min-w-0 flex-1 border-l border-border px-2 py-1.5 text-left transition-colors hover:bg-surface-2",
                  weekend && "bg-surface-2/40",
                  isToday && "bg-accent-50/60"
                )}
              >
                <div className="text-[11px] uppercase tracking-wide text-text-3">
                  {d.toLocaleDateString("de-DE", { weekday: "short" })}
                </div>
                <div
                  className={clsx(
                    "text-sm font-semibold tabular-nums",
                    isToday ? "text-accent-600" : "text-text"
                  )}
                >
                  {d.getDate()}.{d.getMonth() + 1}.
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Ganztägig-Band — nur wenn es welche gibt (kein leerer Streifen) */}
      {hasAllDay && (
        <div data-cal-allday className="flex shrink-0 border-b border-border bg-surface-2/40">
          <div
            className={clsx(
              GUTTER,
              "shrink-0 px-2 py-1.5 text-right text-[10px] uppercase tracking-wide text-text-3"
            )}
          >
            {t.calendar.termin.ganztaegig}
          </div>
          {layouts.map((l) => (
            <div
              key={l.day.getTime()}
              className="min-w-0 flex-1 space-y-1 border-l border-border p-1"
            >
              {l.allDay.map((ev, i) => (
                <button
                  key={`${ev.subject}-${ev.start}-${i}`}
                  type="button"
                  onClick={() => onOpenEvent(ev)}
                  title={ev.subject}
                  // Gleicher Test-Hook wie die Raster-Blöcke: OHNE ihn ist der
                  // Bearbeiten-Pfad ganztägiger Termine aus keinem Walk
                  // erreichbar (`splitDay` schiebt jeden tagfüllenden Termin in
                  // dieses Band) — und genau dieser Fall begründet, warum
                  // EditEventModal ein konkretes Ende führt statt einer Dauer.
                  data-cal-event
                  data-cal-allday
                  className={clsx(
                    "block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium transition-colors",
                    ev.is_cancelled
                      ? "bg-danger-soft/60 text-danger line-through"
                      : "bg-accent-100 text-accent-700 hover:bg-accent-200"
                  )}
                >
                  {ev.subject || t.calendar.termin.keinBetreff}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Raster (eigener Scroll-Container → Kopfzeilen bleiben stehen) */}
      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex" style={{ height: totalPx }}>
          {/* Stunden-Achse */}
          <div className={clsx(GUTTER, "relative shrink-0")}>
            {hours.map((h) => (
              <div
                key={h}
                data-cal-hour={h}
                className="absolute right-2 text-[11px] tabular-nums text-text-3"
                style={{ top: top(h * 60) + 2 }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          {/* Spaltenfläche */}
          <div className="relative flex flex-1">
            {layouts.map((l, dayIdx) => {
              const weekend = l.day.getDay() === 0 || l.day.getDay() === 6;
              const isToday = sameDay(l.day, now);
              const nowMin = now.getHours() * 60 + now.getMinutes();
              const showNow =
                isToday && nowMin >= fromHour * 60 && nowMin <= toHour * 60;
              const dragHere = drag?.dayIdx === dayIdx ? slotFromDrag(drag.from, drag.to) : null;
              const hoverHere =
                !drag && hover?.dayIdx === dayIdx ? slotFromDrag(hover.min, hover.min) : null;

              return (
                <div
                  key={l.day.getTime()}
                  data-cal-column={dayIdx}
                  className={clsx(
                    "relative min-w-0 flex-1 border-l border-border",
                    weekend && "bg-surface-2/30"
                  )}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    const min = minAt(e);
                    e.currentTarget.setPointerCapture(e.pointerId);
                    setDrag({ dayIdx, from: min, to: min });
                  }}
                  onPointerMove={(e) => {
                    // Nur bei echtem Rasterwechsel neu rendern (sonst rendert
                    // jede Mausbewegung das ganze Raster neu).
                    const min = minAt(e);
                    if (drag) {
                      if (drag.to !== min) setDrag({ ...drag, to: min });
                    } else {
                      setHover((prev) =>
                        prev?.dayIdx === dayIdx && prev.min === min
                          ? prev
                          : { dayIdx, min }
                      );
                    }
                  }}
                  onPointerLeave={() => {
                    if (!drag) setHover(null);
                  }}
                  onPointerUp={(e) => {
                    // Der Zeiger ist auf der Start-Spalte gefangen (setPointerCapture),
                    // deshalb zählt `drag.dayIdx`, nicht die Spalte des Ereignisses.
                    if (!drag) return;
                    // Loslass-Punkt = wo der Nutzer geklickt hat → Dialog-Mitte.
                    finishDrag(drag, { x: e.clientX, y: e.clientY });
                    setDrag(null);
                  }}
                  onPointerCancel={() => setDrag(null)}
                >
                  {/* Randzeiten ausserhalb der GESCHÄFTSZEITEN abgeblendet
                      (Begründung + verworfene Alternative: offHoursBands in
                      calendarLayout.ts).

                      Abgeblendet heisst ZURÜCKGENOMMEN, nicht gesperrt: ein
                      Klick um 07:00 legt weiter einen Termin an — genau das
                      sagt der Wizard-Text „auch ausserhalb deiner
                      Geschäftszeiten" zu.

                      Das trägt NICHT `pointer-events-none`. Die Fläche ist ein
                      KIND der Spalte, ihre Zeiger-Ereignisse blasen also
                      ohnehin zum Handler der Spalte hoch, und `minAt` rechnet
                      über `currentTarget` (die Spalte), nicht über `target` —
                      die Minute stimmt in beiden Fällen. Die Zusage hängt
                      allein daran, dass hier NICHTS `stopPropagation()` ruft,
                      anders als die Termin-Blöcke weiter unten, die das
                      absichtlich tun. (Erst gemessen, dann behauptet: die
                      Rot-Probe, die nur `pointer-events-none` entfernt, bleibt
                      GRÜN — der Klick-Beleg kippt erst, wenn die Fläche die
                      Weitergabe unterbindet. Siehe
                      docs/evidence/kalender-runde/rotproben/rot-A5-*.txt.)

                      `pointer-events-none` bleibt trotzdem stehen: eine reine
                      Auszeichnung soll kein Treffer-Ziel sein.

                      Die Bänder liegen OHNE z-index, also unter den Blöcken
                      (z-10). Damit blendet die Fläche einen Termin, der in ihr
                      liegt, NICHT mit ab: seine Farbe deckt. Das ist der
                      Unterschied zwischen „die leere Stunde ist ruhiger" und
                      „mein 07:00-Termin ist schlechter lesbar". */}
                  {offHours.topHours > 0 && (
                    <div
                      data-cal-offhours="top"
                      className={clsx(OFFHOURS_TINT, "top-0 border-b-2")}
                      style={{ height: offHours.topHours * effHourPx }}
                    />
                  )}
                  {offHours.bottomHours > 0 && (
                    <div
                      data-cal-offhours="bottom"
                      className={clsx(OFFHOURS_TINT, "bottom-0 border-t-2")}
                      style={{ height: offHours.bottomHours * effHourPx }}
                    />
                  )}

                  {/* Termin-Blöcke (z-10: über Linien + Schattierung) */}
                  {l.timed.map((seg, i) => {
                    const ev = seg.ev;
                    const h = Math.max(
                      MIN_BLOCK_PX,
                      ((seg.endMin - seg.startMin) / 60) * effHourPx - 2
                    );
                    const start = dateAtMinute(l.day, seg.startMin);
                    const label = `${hm(seg.startMin)}–${hm(seg.endMin)} ${
                      ev.subject || t.calendar.termin.keinBetreff
                    }${ev.location ? ` · ${ev.location}` : ""}`;
                    return (
                      <button
                        key={`${ev.start}-${ev.subject}-${i}`}
                        type="button"
                        title={label}
                        data-cal-event
                        // Auf einem Termin startet kein Aufziehen und es wird
                        // kein „+"-Geist gezeigt — ein Zug DURCH den Termin
                        // hindurch (Drag läuft) bleibt aber erlaubt.
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerEnter={() => {
                          if (!drag) setHover(null);
                        }}
                        onPointerMove={(e) => {
                          if (!drag) e.stopPropagation();
                        }}
                        onClick={() => onOpenEvent(ev)}
                        style={{
                          top: top(seg.startMin) + 1,
                          height: h,
                          left: `calc(${(seg.col / seg.colCount) * 100}% + 2px)`,
                          width: `calc(${(1 / seg.colCount) * 100}% - 4px)`,
                        }}
                        className={clsx(
                          // `overflow-clip` statt `overflow-hidden`: clip macht KEINEN
                          // Scroll-Container auf, deshalb bleibt das `sticky` der
                          // Beschriftung unten am äußeren Raster-Scroller verankert.
                          "absolute z-10 overflow-clip rounded-md border border-l-[3px] px-1.5 text-left transition-colors",
                          h >= TWO_LINE_PX ? "py-0.5" : "py-0",
                          ev.is_cancelled
                            ? "border-danger/40 border-l-danger bg-danger-soft/60 text-danger"
                            : "border-accent-300 border-l-accent-500 bg-accent-50 text-accent-700 hover:bg-accent-100"
                        )}
                      >
                        {/* Beschriftung klebt oben im Block: ein Termin, dessen
                            Beginn über die Scroll-Kante gewandert ist (früher
                            Start, mehrtägiges Stück ab 00:00), bliebe sonst ein
                            leerer Farbblock ohne Text. */}
                        <span data-cal-event-label className="sticky top-0 block">
                          {h >= TWO_LINE_PX ? (
                            <>
                              <span className="block truncate text-[11px] tabular-nums opacity-80">
                                {fmtHM(start)}–{hm(seg.endMin)}
                              </span>
                              <span
                                className={clsx(
                                  "block truncate text-xs font-medium",
                                  ev.is_cancelled && "line-through"
                                )}
                              >
                                {ev.subject || t.calendar.termin.keinBetreff}
                              </span>
                              {h >= THREE_LINE_PX && ev.location && (
                                <span className="block truncate text-[11px] opacity-75">
                                  {ev.location}
                                </span>
                              )}
                            </>
                          ) : (
                            <span
                              className={clsx(
                                "block truncate text-[11px] leading-[15px]",
                                ev.is_cancelled && "line-through"
                              )}
                            >
                              <span className="tabular-nums opacity-80">
                                {fmtHM(start)}
                              </span>{" "}
                              <span className="font-medium">
                                {ev.subject || t.calendar.termin.keinBetreff}
                              </span>
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}

                  {/* Hover-Vorschau: zeigt, welcher Slot ein Klick anlegen würde */}
                  {hoverHere && (
                    <div
                      className="pointer-events-none absolute inset-x-0.5 z-[5] flex items-center gap-1 rounded-md border border-dashed border-accent-400 bg-accent-50/50 px-1.5 text-[11px] text-accent-600"
                      style={{
                        top: top(hoverHere.startMin),
                        height:
                          ((hoverHere.endMin - hoverHere.startMin) / 60) * effHourPx,
                      }}
                    >
                      <Plus className="h-3 w-3 shrink-0" />
                      <span className="truncate tabular-nums">
                        {hm(hoverHere.startMin)}
                      </span>
                    </div>
                  )}

                  {/* Zieh-Vorschau */}
                  {dragHere && (
                    <div
                      className="pointer-events-none absolute inset-x-0.5 z-20 rounded-md border border-accent-500 bg-accent-500/20 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-accent-700"
                      style={{
                        top: top(dragHere.startMin),
                        height: Math.max(
                          MIN_BLOCK_PX,
                          ((dragHere.endMin - dragHere.startMin) / 60) * effHourPx
                        ),
                      }}
                    >
                      {hm(dragHere.startMin)}–{hm(dragHere.endMin)}
                    </div>
                  )}

                  {/* „Jetzt"-Linie (nur in der heutigen Spalte) */}
                  {showNow && (
                    <div
                      data-cal-now
                      className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
                      style={{ top: top(nowMin) }}
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full bg-danger" />
                      <span className="h-px flex-1 bg-danger" />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Stunden-/Halbstundenlinien EINMAL über der Spaltenfläche: liegen
                über den Hintergründen, aber unter den Blöcken (z-10). */}
            <div className="pointer-events-none absolute inset-0">
              {hours.map((h, i) => (
                <div key={h}>
                  {i > 0 && (
                    <div
                      className="absolute inset-x-0 border-t border-border"
                      style={{ top: top(h * 60) }}
                    />
                  )}
                  <div
                    className="absolute inset-x-0 border-t border-border/40"
                    style={{ top: top(h * 60 + 30) }}
                  />
                </div>
              ))}
            </div>

            {loading && (
              <div className="absolute inset-0 z-30 bg-canvas/50" aria-hidden />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarTimeGrid;
